import React from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { t } from "@lingui/macro";

const EditPriceForm = ({ price, onSave, onCancel, onDelete }) => {
  if (!price) {
    return <div>{t`Error: Invalid price data`}</div>;
  }

  const validationSchema = Yup.object({
    type: Yup.string()
      .oneOf(["ticket", "room"], t`Invalid type`)
      .required(t`Type is required`),

    priceCategory: Yup.string().required(t`Price category is required`),

    typeEn: Yup.string().required(t`Type (EN) is required`),
    typeEs: Yup.string().required(t`Type (ES) is required`),

    priceCategoryEn: Yup.string().required(t`Price category (EN) is required`),
    priceCategoryEs: Yup.string().required(t`Price category (ES) is required`),

    price: Yup.number()
      .required(t`Price is required`)
      .positive(t`Price must be positive`),

    currency: Yup.string()
      .required(t`Currency is required`)
      .matches(/^[A-Z]{3}$/, t`Currency must be 3-letter code (e.g. MXN)`),
  });

  const handleSubmit = (values) => {
    onSave(values);
  };

  return (
    <div className="form-container">
      <h2>{price.id ? t`Edit Price` : t`Add New Price`}</h2>

      <Formik
        initialValues={{
          type: price.type || "",
          priceCategory: price.priceCategory || "",

          typeEn: price.typeEn || "",
          typeEs: price.typeEs || "",

          priceCategoryEn: price.priceCategoryEn || "",
          priceCategoryEs: price.priceCategoryEs || "",

          price: price.price || "",
          currency: price.currency || "MXN",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => (
          <Form>
            {/* ===== BACKEND LOGIC ===== */}
            <h3>{t`Backend keys (logic)`}</h3>

            <div className="form-row">
              <label>{t`Type`} *</label>
              <Field
                as="select"
                name="type"
                className="form-control"
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue("type", value);

                  // Automatyczne tłumaczenia
                  if (value === "ticket") {
                    setFieldValue("typeEn", "Ticket");
                    setFieldValue("typeEs", "Boleto");
                  }

                  if (value === "room") {
                    setFieldValue("typeEn", "Room");
                    setFieldValue("typeEs", "Habitación");
                  }
                }}
              >
                <option value="">{t`Select type`}</option>
                <option value="ticket">Ticket</option>
                <option value="room">Room</option>
              </Field>
              <ErrorMessage name="type" component="div" className="form-control-feedback" />
            </div>

            <div className="form-row">
              <label>{t`Price Category`} *</label>
              <Field name="priceCategory" className="form-control" />
              <ErrorMessage name="priceCategory" component="div" className="form-control-feedback" />
            </div>

            {/* ===== DISPLAY (TRANSLATIONS) ===== */}
            <h3>{t`Display names`}</h3>

            <div className="form-row">
              <label>{t`Type (English)`}</label>
              <Field name="typeEn" className="form-control" />
              <ErrorMessage name="typeEn" component="div" className="form-control-feedback" />
            </div>

            <div className="form-row">
              <label>{t`Type (Spanish)`}</label>
              <Field name="typeEs" className="form-control" />
              <ErrorMessage name="typeEs" component="div" className="form-control-feedback" />
            </div>

            <div className="form-row">
              <label>{t`Price Category (English)`}</label>
              <Field name="priceCategoryEn" className="form-control" />
              <ErrorMessage name="priceCategoryEn" component="div" className="form-control-feedback" />
            </div>

            <div className="form-row">
              <label>{t`Price Category (Spanish)`}</label>
              <Field name="priceCategoryEs" className="form-control" />
              <ErrorMessage name="priceCategoryEs" component="div" className="form-control-feedback" />
            </div>

            {/* ===== FINANCIAL DATA ===== */}
            <h3>{t`Price data`}</h3>

            <div className="form-row">
              <label>{t`Price`} *</label>
              <Field name="price" type="number" className="form-control" />
              <ErrorMessage name="price" component="div" className="form-control-feedback" />
            </div>

            <div className="form-row">
              <label>{t`Currency`} *</label>
              <Field name="currency" className="form-control" />
              <ErrorMessage name="currency" component="div" className="form-control-feedback" />
            </div>

            {/* ===== ACTIONS ===== */}
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {t`Save`}
              </button>

              <button type="button" onClick={onCancel} className="cancel-btn">
                {t`Cancel`}
              </button>

              {price.id && onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(price.id)}
                  className="delete-btn"
                  title={t`Delete`}
                >
                  🗑️
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditPriceForm;
